import { FutureTabContent } from "@/components/patients/FutureTabContent";
import { VaccinationFormDialog } from "@/components/applications/ApplicationFormDialog";
import { buildUpcomingContextFromRow, type UpcomingApplicationDialogContext } from "@/components/applications/upcoming-dialog-context";
import { AppointmentDetailsDialog } from "@/components/calendar/AppointmentDetailsDialog";
import { useFutureRows, type OwnerPetSummary } from "@/hooks/useFutureRows";
import { useState } from "react";
import { useTranslation } from "react-i18next";

interface OwnerFutureTabProps {
  ownerId: string;
  petIds: string[];
  petSummaries?: OwnerPetSummary[];
  clinicId: string | null;
  includeGrooming?: boolean;
}

export function OwnerFutureTab({
  ownerId,
  petIds,
  petSummaries,
  clinicId,
  includeGrooming = true,
}: OwnerFutureTabProps) {
  const { t } = useTranslation("patients");
  const [selectedUpcomingItem, setSelectedUpcomingItem] = useState<UpcomingApplicationDialogContext | null>(null);
  const [selectedAppointmentId, setSelectedAppointmentId] = useState<string | null>(null);
  const { pendingRows, upcomingRows, isLoading } = useFutureRows({
    scope: "owner",
    ownerId,
    petIds,
    petSummaries,
    clinicId,
    includeGrooming,
  });

  return (
    <>
      <FutureTabContent
        pendingRows={pendingRows}
        upcomingRows={upcomingRows}
        isLoading={isLoading}
        showClinicHint={!clinicId}
        showPetColumn
        pendingEmptyMessage={t("futureTab.emptyPendingOwner")}
        upcomingEmptyMessage={t("futureTab.emptyOwner")}
        onVaccinationSelect={(vaccination, row) => {
          if (!row.petId) return;
          setSelectedUpcomingItem(buildUpcomingContextFromRow({ petId: row.petId, vaccination }));
        }}
        onPlanSelect={(plan, row) => {
          if (!row.petId) return;
          setSelectedUpcomingItem(buildUpcomingContextFromRow({ petId: row.petId, plan }));
        }}
        onAppointmentSelect={(row) => {
          if (!row.appointmentId) return;
          setSelectedAppointmentId(row.appointmentId);
        }}
      />
      {selectedAppointmentId ? (
        <AppointmentDetailsDialog
          appointmentId={selectedAppointmentId}
          open={Boolean(selectedAppointmentId)}
          onOpenChange={(open) => {
            if (!open) setSelectedAppointmentId(null);
          }}
        />
      ) : null}
      {selectedUpcomingItem && clinicId ? (
        <VaccinationFormDialog
          open={Boolean(selectedUpcomingItem)}
          onOpenChange={(open) => {
            if (!open) {
              setSelectedUpcomingItem(null);
            }
          }}
          petId={selectedUpcomingItem.petId}
          clinicId={clinicId}
          upcomingContext={selectedUpcomingItem}
          initialTab="record"
          showCreateAppointmentToggle
        />
      ) : null}
    </>
  );
}
